<template lang="pug">
  div.snapshot-wrapper(
    :style="{ top: snapshotTopPosition + 'px' }"
    ref="snapshotWrapper"
  )
    .loading-dots(v-if="loading")
      .dot1
      .dot2
      .dot3
    transition(name="snapshot-anim" v-else-if="patient")
      .snapshot
        h3
          router-link.link-highlight(:to="{ name: 'Patient', params: { id: patient.id }}")
            | {{ patient.firstName }} {{ patient.lastName }}
        p {{ patient.emailAddress }}
        p.error(v-if="!patient.profileComplete") Profile Incomplete
        p
          strong Access Code:&nbsp;
          | {{ patient.accessCode }}
        p
          strong Referrer:&nbsp;
          | {{ patient.referrerName || 'No referrer' }}
        p
          strong Current Focus:&nbsp;
          | {{ patient.currentFocus }}
        assessments(:scores="patient.assessmentScores" :noDataMsg="noDataMsg")
        tracker-chart(:tracker="defaultTracker" :noDataMsg="noDataMsg")
        dl
          dt Recent User Goal
          dd(v-if="!recentGoal")
            | {{ noDataMsg }}
          dd(v-if="recentGoal")
            p &ldquo;{{ recentGoal }}&rdquo;
        interaction-history(:loginHistory="patient.loginHistory" :showRecentOnly="true" :patientAge="patient.age" :patientGender="patient.gender")
        dl
          dt Latest Review
          dd(v-if="patient.lastReviewedByEmail") User was reviewed by {{ patient.lastReviewedByEmail }} on {{ patient.lastReviewed | shortDate }}
          dd(v-if="!patient.lastReviewedByEmail") Not reviewed
        ul.actions
          li
            router-link.btn(
              title="View member profile"
              :to="{ name: 'Patient', params: { id: patient.id }}")
              | View Member Detail
          li
            button.btn(@click="markReviewed(patient.id)") Mark as Reviewed
    .empty-snapshot(v-else)
      p Click on a user row to view a snapshot of their recent data.
      img(src="/static/images/empty-snapshot.svg")
</template>

<script>
import Assessments from './snapshot/Assessments';
import TrackerChart from './snapshot/TrackerChart';
import InteractionHistory from './common/InteractionHistory';
import _ from 'lodash';

export default {
  name: 'PatientSnapshot',
  components: {
    Assessments,
    TrackerChart,
    InteractionHistory
  },
  data() {
    return {
      noDataMsg: 'No data captured',
      snapshotTopPosition: 0
    };
  },
  props: {
    patient: {
      required: false
    },
    loading: {
      type: Boolean
    },
    markReviewed: {
      type: Function,
      required: true
    }
  },
  computed: {
    recentGoal: function() {
      return _.get(this.patient, 'goals[0]');
    },
    defaultTracker: function() {
      // return emotional health data
      if (this.patient && this.patient.trackerHistory) {
        return _.find(this.patient.trackerHistory, 'isRequired');
      }
      return {};
    }
  },
  methods: {
    setSnapshotPosition() {
      // Get html scroll position and snapshot offset
      var htmlScrollPosition = parseInt(window.pageYOffset);
      var snapshotOffset = this.$refs.snapshotWrapper.parentElement.offsetTop;
      var newSnapshotTopPosition = htmlScrollPosition - snapshotOffset;

      // Set snapshot top position
      this.snapshotTopPosition = newSnapshotTopPosition >= 0 ? newSnapshotTopPosition : 0;
    }
  },
  watch: {
    loading() {
      this.setSnapshotPosition();
    },
    patient() {
      this.setSnapshotPosition();
    }
  }
};
</script>

<style lang="scss">
  @keyframes fade-in {
    0% { opacity: 0 };
    100% { opacity: 1 };
  }
  .snapshot-wrapper {
    position: relative;
    margin-bottom: 2em;
  }
  .empty-snapshot {
    text-align: center;
    color: $dark-color;
    animation: fade-in 2.5s ease-in;
    p {
      position: relative;
      font-family: $serif-font-family;
      font-size: $base-font-size;
      line-height: 1.25;
      @include zero;
      padding: $small-spacing;
      background: $base-background-color;
      border: 1px solid #fff;
      @include drop-shadow;
      border-bottom: 1px solid $dark-color;
      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        @include size(10px);
        margin: 0 0 -5px -5px;
        background: $dark-color;
        border-radius: 5px;
      }
    }
    img {
      @include zero;
      display: block;
      width: 50%;
      margin-right: auto;
      position: relative;
      margin-top: -6px;
      z-index: -1;
    }
  }
  .snapshot {
    background: #fff;
    padding: $small-spacing;
    @include drop-shadow;
    .error {
      font-size: $ms-down1;
      color: $error-color;
    }
    p {
      font-size: $ms-down1;
      line-height: 1.2;
      strong {
        font-weight: $base-font-weight-strong;
      }
    }
    dl {
      @include zero;
      margin-bottom: $small-spacing;
      &:last-of-type {
        margin-bottom: 0;
      }
    }
    dt {
      font-size: $ms-down1;
      font-weight: $base-font-weight-strong;
      text-transform: uppercase;
      border-top: 1px solid $black;
      margin: 0 0 .25em 0;
    }
    dd {
      font-size: $ms-down1;
      line-height: 1.2;
      margin-bottom: .25em;
    }
    table {
      margin: 0 auto;
      border-top: 1px solid $base-border-color;
    }
    tr:last-of-type {
      td { border: none; }
    }
    ul.actions {
      margin: $base-spacing auto;
      li {
        display: block;
        font-size: $ms-down1;
        margin-bottom: .5em;
      }
      .btn {
        width: 100%;
        text-align: center;
        font-family: $base-font-family;
        font-weight: $base-font-weight-strong;
      }
    }

  }

  // TRANSITIONS
  .snapshot-anim-enter-active {
    transition: all .3s ease;
  }
  .snapshot-anim-leave-active {
    transition: all .25s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }
  .snapshot-anim-enter, .snapshot-anim-leave-to {
    transform: translateX(100%);
    opacity: 0;
  }

</style>
